'use client';

import moment from 'moment';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as qs from 'qs';
import { useHistory, useLocation } from 'react-router';
import { UseFilter } from '../../shared/hooks/UseExcel';

function EnhancedFilterDateComponent({
  link,
  showOwn,
  searchParams,
  searchField,
}) {
  const history = useHistory();
  const location = useLocation();
  // UseFilter hook for date filter logic (pass location.search for reactivity)
  const [activeBtn, todayDate, weekStartDate, weekEndsDate] = UseFilter(
    location.search
  );
  const [customDataSelected, setCustomDataSelected] = useState(false);
  const [dateConditions, setDateConditions] = useState(null);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  useEffect(() => {
    const queryParams = qs.parse(location.search.replace('?', ''));
    if (
      queryParams.conditional &&
      queryParams.conditional[searchField ? searchField : 'createdAt']
    ) {
      setDateConditions(
        queryParams.conditional[searchField ? searchField : 'createdAt']
      );
    } else {
      setDateConditions(null);
    }
  }, [location.search, searchField]);

  const handleSidebarChangeArray = (fieldsArray, dateType) => {
    const queryParams = qs.parse(location.search.replace('?', ''));
    if (fieldsArray) {
      fieldsArray.forEach(({ name, value, type = 'search', condition }) => {
        if (type === 'conditional') {
          if (queryParams[type]) {
            if (queryParams[type][name]) {
              if (value && value.length > 0) {
                queryParams[type][name][condition ? condition : '$gte'] = value;
              } else {
                delete queryParams[type][name][condition ? condition : '$gte'];
              }
            } else {
              queryParams[type][name] = {};
              if (value && value.length > 0) {
                queryParams[type][name][condition ? condition : '$gte'] = value;
              } else {
                delete queryParams[type][name][condition ? condition : '$gte'];
              }
            }
          } else {
            queryParams[type] = {};
            queryParams[type][name] = {};
            if (value && value.length > 0) {
              queryParams[type][name][condition ? condition : '$gte'] = value;
            } else {
              delete queryParams[type][name][condition ? condition : '$gte'];
            }
          }
        }
      });
    }

    queryParams.date_type = dateType;
    const query = qs.stringify(queryParams, {
      encodeValuesOnly: true,
    });
    history.push(`${location.pathname}?${query}`);
  };

  const getFilterDisplayName = () => {
    switch (activeBtn) {
      case 'today':
        return 'Today';
      case 'yesterday':
        return 'Yesterday';
      case 'weekly':
        return 'This Week';
      case 'monthly':
        return 'This Month';
      case 'last_month':
        return 'Last Month';
      case 'Custom':
        return 'Custom Range';
      case 'Lifetime':
        return 'All Time';
      default:
        return 'All Time';
    }
  };

  const getDateRangeDisplay = () => {
    if (dateConditions?.['$gte'] && dateConditions?.['$lte']) {
      return `${moment(dateConditions['$gte']).format('MMM DD')} - ${moment(
        dateConditions['$lte']
      ).format('MMM DD')}`;
    } else if (dateConditions?.['$gte'] && dateConditions?.['$lt']) {
      const endDate = moment(dateConditions['$lt']).subtract(1, 'day');
      return `${moment(dateConditions['$gte']).format(
        'MMM DD'
      )} - ${endDate.format('MMM DD')}`;
    }
    return '';
  };

  const isMyActivityActive = () => {
    if (!showOwn || !searchParams) return false;
    const queryParams = qs.parse(location.search.replace('?', ''));
    return (
      queryParams.exact &&
      queryParams.exact[searchParams.term] === searchParams.value
    );
  };

  // Helper to build My Activity link without duplicate exact param
  const getMyActivityLink = () => {
    const queryParams = qs.parse(location.search.replace('?', ''));
    if (
      queryParams.exact &&
      queryParams.exact[searchParams.term] === searchParams.value
    ) {
      // Already present, return current link
      return location.pathname + location.search;
    } else {
      // Append or add exact param
      let newQuery = { ...queryParams };
      newQuery.exact = {
        ...newQuery.exact,
        [searchParams.term]: searchParams.value,
      };
      return (
        location.pathname +
        '?' +
        qs.stringify(newQuery, { encodeValuesOnly: true })
      );
    }
  };

  return (
    <>
      <style jsx>{`
        .date-filter-compact {
          margin: 16px 0;
        }

        .filter-main {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-radius: 10px;
          padding: 12px 16px;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
          transition: border-color 0.2s, box-shadow 0.2s;
        }
        .filter-main.active {
          /* Removed blue border and shadow */
          border: 1px solid #e2e8f0;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
        }

        .filter-label-compact {
          font-size: 12px;
          font-weight: 600;
          color: #64748b;
          display: flex;
          align-items: center;
          gap: 6px;
          white-space: nowrap;
        }

        .filter-icon-compact {
          width: 16px;
          height: 16px;
          background: #64748b;
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 8px;
        }

        .filter-buttons-compact {
          display: flex;
          align-items: center;
          gap: 6px;
          flex-wrap: wrap;
          flex: 1;
        }

        .filter-btn-mini {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          color: #64748b;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          transition: all 0.2s ease;
          cursor: pointer;
          text-decoration: none;
          white-space: nowrap;
          display: flex;
          align-items: center;
          gap: 4px;
          position: relative;
          min-height: 28px;
        }

        .filter-btn-mini:hover {
          background: #f1f5f9;
          border-color: #cbd5e1;
          color: #475569;
          text-decoration: none;
        }

        .filter-btn-mini.active {
          /* Highlight active button with green background and white text */
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-color: #10b981;
          color: #fff;
          box-shadow: 0 4px 12px rgba(16, 185, 129, 0.18),
            0 2px 6px rgba(16, 185, 129, 0.1);
          font-weight: 700;
          transform: scale(1.07);
          z-index: 1;
        }

        .filter-btn-mini.active::after {
          content: '●';
          position: absolute;
          top: -3px;
          right: -3px;
          background: #10b981;
          color: white;
          width: 12px;
          height: 12px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 6px;
          border: 1px solid white;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0% {
            transform: scale(1);
          }
          50% {
            transform: scale(1.1);
          }
          100% {
            transform: scale(1);
          }
        }

        .applied-filter-compact {
          background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
          border: 1px solid #10b981;
          color: #065f46;
          padding: 4px 10px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
        }

        .clear-btn-mini {
          background: #fee2e2;
          border: 1px solid #fca5a5;
          color: #dc2626;
          padding: 6px 12px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          gap: 4px;
          white-space: nowrap;
          min-height: 28px;
        }

        .clear-btn-mini:hover {
          background: #fecaca;
          color: #dc2626;
          text-decoration: none;
        }

        .custom-date-compact {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #eff6ff;
          border: 1px solid #bae6fd;
          border-radius: 8px;
          padding: 8px 12px;
          margin-left: 8px;
          flex-wrap: wrap;
        }

        .date-input-mini {
          border: 1px solid #cbd5e1;
          border-radius: 4px;
          padding: 4px 8px;
          font-size: 11px;
          background: white;
          width: 110px;
          font-weight: 500;
        }

        .date-input-mini:focus {
          outline: none;
          border-color: #3b82f6;
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1);
        }

        .action-btn-mini {
          background: #10b981;
          color: white;
          border: none;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .action-btn-mini:hover {
          background: #059669;
        }

        .action-btn-mini:disabled {
          background: #d1d5db;
          cursor: not-allowed;
        }

        .cancel-btn-mini {
          background: #f3f4f6;
          color: #6b7280;
          border: 1px solid #d1d5db;
          padding: 4px 10px;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .cancel-btn-mini:hover {
          background: #e5e7eb;
        }

        .divider-mini {
          width: 1px;
          height: 16px;
          background: #e2e8f0;
          margin: 0 4px;
        }

        @media (max-width: 768px) {
          .filter-main {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
            padding: 10px 14px;
          }

          .filter-buttons-compact {
            width: 100%;
            justify-content: flex-start;
          }

          .filter-btn-mini {
            flex: 1;
            min-width: 0;
            justify-content: center;
          }

          .custom-date-compact {
            width: 100%;
            margin-left: 0;
            justify-content: space-between;
          }

          .date-input-mini {
            width: 100px;
          }
        }

        @media (max-width: 480px) {
          .filter-buttons-compact {
            flex-direction: column;
            width: 100%;
          }

          .filter-btn-mini {
            width: 100%;
          }

          .custom-date-compact {
            flex-direction: column;
            gap: 6px;
          }

          .date-input-mini {
            width: 100%;
          }
        }
      `}</style>

      <div className='container-fluid'>
        <div className='date-filter-compact'>
          <div
            className={`filter-main${
              (activeBtn && activeBtn !== 'Lifetime') || isMyActivityActive()
                ? ' active'
                : ''
            }`}
          >
            <div className='filter-label-compact'>
              <span className='filter-icon-compact'>
                <i className='fa fa-calendar'></i>
              </span>
              Filter:
            </div>

            {customDataSelected ? (
              <div className='custom-date-compact'>
                <input
                  type='date'
                  className='date-input-mini'
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
                <span style={{ fontSize: '10px', color: '#64748b' }}>to</span>
                <input
                  type='date'
                  className='date-input-mini'
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
                <button
                  className='action-btn-mini'
                  onClick={() => {
                    handleSidebarChangeArray(
                      [
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(fromDate).format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$gte',
                        },
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(toDate)
                            .add(1, 'days')
                            .format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$lt',
                        },
                      ],
                      'Custom'
                    );
                    setCustomDataSelected(false);
                  }}
                  disabled={!fromDate || !toDate}
                >
                  Apply
                </button>
                <button
                  className='cancel-btn-mini'
                  onClick={() => {
                    setCustomDataSelected(false);
                    setFromDate('');
                    setToDate('');
                  }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div className='filter-buttons-compact'>
                <button
                  className={`filter-btn-mini ${
                    activeBtn === 'today' ? 'active' : ''
                  }`}
                  onClick={() => {
                    handleSidebarChangeArray(
                      [
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(todayDate).format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$gte',
                        },
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(todayDate)
                            .add(1, 'days')
                            .format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$lt',
                        },
                      ],
                      'today'
                    );
                  }}
                >
                  Today
                </button>

                <button
                  className={`filter-btn-mini ${
                    activeBtn === 'yesterday' ? 'active' : ''
                  }`}
                  onClick={() => {
                    handleSidebarChangeArray(
                      [
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(todayDate)
                            .subtract(1, 'days')
                            .format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$gte',
                        },
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(todayDate).format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$lt',
                        },
                      ],
                      'yesterday'
                    );
                  }}
                >
                  Yesterday
                </button>

                <button
                  className={`filter-btn-mini ${
                    activeBtn === 'weekly' ? 'active' : ''
                  }`}
                  onClick={() => {
                    handleSidebarChangeArray(
                      [
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(todayDate)
                            .startOf('week')
                            .add(1, 'days')
                            .format('YYYY-MM-DD'), // Monday
                          type: 'conditional',
                          condition: '$gte',
                        },
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment(todayDate)
                            .add(1, 'days')
                            .format('YYYY-MM-DD'), // Tomorrow
                          type: 'conditional',
                          condition: '$lt',
                        },
                      ],
                      'weekly'
                    );
                  }}
                >
                  This Week
                </button>

                <button
                  className={`filter-btn-mini ${
                    activeBtn === 'monthly' ? 'active' : ''
                  }`}
                  onClick={() => {
                    handleSidebarChangeArray(
                      [
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment().startOf('month').format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$gte',
                        },
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment().endOf('month').format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$lte',
                        },
                      ],
                      'monthly'
                    );
                  }}
                >
                  This Month
                </button>

                <button
                  className={`filter-btn-mini ${
                    activeBtn === 'last_month' ? 'active' : ''
                  }`}
                  onClick={() => {
                    handleSidebarChangeArray(
                      [
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment()
                            .subtract(1, 'months')
                            .startOf('month')
                            .format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$gte',
                        },
                        {
                          name: searchField ? searchField : 'createdAt',
                          value: moment()
                            .subtract(1, 'months')
                            .endOf('month')
                            .format('YYYY-MM-DD'),
                          type: 'conditional',
                          condition: '$lte',
                        },
                      ],
                      'last_month'
                    );
                  }}
                >
                  Last Month
                </button>

                <button
                  className={`filter-btn-mini ${
                    activeBtn === 'Custom' ? 'active' : ''
                  }`}
                  onClick={() => {
                    setCustomDataSelected(true);
                  }}
                >
                  Custom
                </button>

                <Link
                  to={link}
                  className={`filter-btn-mini ${
                    activeBtn === 'Lifetime' ? 'active' : ''
                  }`}
                >
                  All Time
                </Link>

                {showOwn && searchParams && (
                  <>
                    <div className='divider-mini'></div>
                    <Link
                      to={getMyActivityLink()}
                      className={`filter-btn-mini ${
                        isMyActivityActive() ? 'active' : ''
                      }`}
                    >
                      <i
                        className='fa fa-user'
                        style={{ fontSize: '11px' }}
                      ></i>
                      My Activity
                    </Link>
                  </>
                )}
              </div>
            )}

            {((activeBtn && activeBtn !== 'Lifetime') ||
              dateConditions?.['$gte'] ||
              dateConditions?.['$lte'] ||
              isMyActivityActive()) && (
              <div className='applied-filter-compact'>
                <i
                  className='fa fa-check-circle'
                  style={{ fontSize: '8px' }}
                ></i>
                {/* Show date filter if active */}
                {(activeBtn && activeBtn !== 'Lifetime') ||
                dateConditions?.['$gte'] ||
                dateConditions?.['$lte'] ? (
                  <span>
                    {getFilterDisplayName()}
                    {getDateRangeDisplay() && (
                      <span>({getDateRangeDisplay()})</span>
                    )}
                  </span>
                ) : null}
                {/* Show separator if both are active */}
                {((activeBtn && activeBtn !== 'Lifetime') ||
                  dateConditions?.['$gte'] ||
                  dateConditions?.['$lte']) &&
                  isMyActivityActive() && (
                    <span style={{ margin: '0 4px', color: '#065f46' }}>+</span>
                  )}
                {/* Show My Activity if active */}
                {isMyActivityActive() && <span>My Activity</span>}
              </div>
            )}

            <Link className='clear-btn-mini' to={link}>
              <i className='fa fa-times' style={{ fontSize: '9px' }}></i>
              Clear
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}

export default EnhancedFilterDateComponent;
